/**
 * Settings > Webhooks — incoming webhook audit (D1 webhook_log). Super Admin only.
 */
import { useState, useEffect, useCallback, type CSSProperties } from 'react';
import { useAuth } from '@clerk/clerk-react';

type WebhookRow = {
  id: number;
  created_at: string;
  source: string;
  event_type?: string;
  ok: boolean;
  reason?: string;
  activity_id?: string;
  agent_id?: number;
  campaign_id?: number;
  http_status?: number;
};

type Summary = {
  calls_in_db: number;
  calls_latest_timestamp: string | null;
  abandoned_in_db: number;
  ipscape_webhooks_last_hour: number;
  wildix_webhooks_last_hour: number;
  last_ipscape_webhook: {
    at: string;
    event_type: string | null;
    ok: boolean;
    reason: string | null;
  } | null;
  webhook_urls: { ipscape: string; wildix: string } | null;
};

function formatLocal(isoOrSql: string): string {
  const d = new Date(isoOrSql.includes('T') ? isoOrSql : `${isoOrSql.replace(' ', 'T')}Z`);
  if (Number.isNaN(d.getTime())) return isoOrSql;
  return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'medium' });
}

export default function SettingsWebhooks() {
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<WebhookRow[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [sourceFilter, setSourceFilter] = useState<'all' | 'ipscape' | 'wildix'>('all');
  const [retentionDays, setRetentionDays] = useState(7);
  const [savingRetention, setSavingRetention] = useState(false);
  const [retentionMsg, setRetentionMsg] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const apiBase = typeof window !== 'undefined' ? window.location.origin : '';

  const loadLogs = useCallback(async (sourceOverride?: 'all' | 'ipscape' | 'wildix') => {
    const token = await getToken();
    if (!token) throw new Error('Not signed in');
    const src = sourceOverride ?? sourceFilter;
    const params = new URLSearchParams({ limit: '200' });
    if (src !== 'all') params.set('source', src);
    const res = await fetch(`${apiBase}/api/webhook-log?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = (await res.json().catch(() => ({}))) as {
      error?: string;
      rows?: WebhookRow[];
      total?: number;
      summary?: Summary;
    };
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    setRows(Array.isArray(data.rows) ? data.rows : []);
    setTotal(typeof data.total === 'number' ? data.total : 0);
    if (data.summary) setSummary(data.summary);
  }, [getToken, apiBase, sourceFilter]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = await getToken();
      if (!token || cancelled) {
        if (!cancelled) setLoading(false);
        return;
      }
      setError(null);
      try {
        const [logRes, cfgRes] = await Promise.all([
          fetch(`${apiBase}/api/webhook-log?limit=200`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${apiBase}/api/config?keys=webhook_log_retention_days`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        const logData = (await logRes.json().catch(() => ({}))) as {
          error?: string;
          rows?: WebhookRow[];
          total?: number;
          summary?: Summary;
        };
        if (!logRes.ok) throw new Error(logData.error || `HTTP ${logRes.status}`);
        if (!cancelled) {
          setRows(Array.isArray(logData.rows) ? logData.rows : []);
          setTotal(typeof logData.total === 'number' ? logData.total : 0);
          if (logData.summary) setSummary(logData.summary);
        }
        if (cfgRes.ok) {
          const cfg = (await cfgRes.json()) as { webhook_log_retention_days?: string };
          const d = parseInt(cfg.webhook_log_retention_days ?? '7', 10);
          if (!cancelled && d >= 1 && d <= 30) setRetentionDays(d);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, apiBase]);

  const saveRetention = async () => {
    const token = await getToken();
    if (!token) return;
    setSavingRetention(true);
    setRetentionMsg(null);
    try {
      const res = await fetch(`${apiBase}/api/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ webhook_log_retention_days: retentionDays }),
      });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setRetentionMsg('Saved.');
    } catch (e) {
      setRetentionMsg(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSavingRetention(false);
    }
  };

  const thStyle: CSSProperties = {
    padding: '0.45rem 0.5rem',
    textAlign: 'left',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'var(--text-muted)',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    whiteSpace: 'nowrap',
  };
  const tdStyle: CSSProperties = {
    padding: '0.45rem 0.5rem',
    fontSize: '0.8125rem',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    verticalAlign: 'top',
  };

  const noIpscapeRecently = summary && summary.ipscape_webhooks_last_hour === 0;

  return (
    <div>
      <h2 style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>Webhooks</h2>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '1rem', maxWidth: 720 }}>
        Every POST to the Presence worker webhook endpoints is logged here (event type and outcome only — no phone numbers or
        payloads). Use this to confirm ipSCAPE is reaching your dashboard. Call history needs{' '}
        <code style={{ background: 'rgba(255,255,255,0.08)', padding: '0.15em 0.35em', borderRadius: 4 }}>agent.wrap</code>{' '}
        events with reason <strong>wrap_stored</strong> or <strong>wrap_duplicate</strong>.
      </p>

      {summary?.webhook_urls && (
        <div
          style={{
            marginBottom: '1rem',
            padding: '0.75rem 1rem',
            background: 'var(--widget-bg)',
            borderRadius: 8,
            border: '1px solid rgba(255,255,255,0.08)',
            fontSize: '0.8125rem',
          }}
        >
          <div style={{ marginBottom: '0.35rem', fontWeight: 600 }}>Configure in ipSCAPE / Wildix</div>
          <div style={{ marginBottom: '0.25rem' }}>
            <span style={{ color: 'var(--text-muted)' }}>ipSCAPE: </span>
            <code style={{ wordBreak: 'break-all' }}>{summary.webhook_urls.ipscape}</code>
          </div>
          <div>
            <span style={{ color: 'var(--text-muted)' }}>Wildix: </span>
            <code style={{ wordBreak: 'break-all' }}>{summary.webhook_urls.wildix}</code>
          </div>
        </div>
      )}

      {summary && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            gap: '0.75rem',
            marginBottom: '1rem',
          }}
        >
          <Stat label="Calls in DB" value={String(summary.calls_in_db)} />
          <Stat label="Abandoned in DB" value={String(summary.abandoned_in_db)} />
          <Stat label="ipSCAPE (last hour)" value={String(summary.ipscape_webhooks_last_hour)} highlight={!!noIpscapeRecently} />
          <Stat label="Wildix (last hour)" value={String(summary.wildix_webhooks_last_hour)} />
        </div>
      )}

      {noIpscapeRecently && (
        <p
          style={{
            marginBottom: '1rem',
            padding: '0.75rem 1rem',
            background: 'rgba(239, 68, 68, 0.12)',
            border: '1px solid rgba(239, 68, 68, 0.35)',
            borderRadius: 8,
            fontSize: '0.875rem',
            color: '#fca5a5',
          }}
        >
          No ipSCAPE webhooks in the last hour. If agents are wrapping calls, check the URL in ipSCAPE Workspace matches the
          endpoint above and that events include <strong>agent.wrap</strong>. Wrap a test call and refresh this page.
        </p>
      )}

      {summary?.last_ipscape_webhook && (
        <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
          Last ipSCAPE webhook: {formatLocal(summary.last_ipscape_webhook.at)} — {summary.last_ipscape_webhook.event_type ?? '?'}{' '}
          / {summary.last_ipscape_webhook.reason ?? '—'} ({summary.last_ipscape_webhook.ok ? 'OK' : 'failed'})
          {summary.calls_latest_timestamp && (
            <> · Latest call row: {formatLocal(summary.calls_latest_timestamp)}</>
          )}
        </p>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center', marginBottom: '1rem' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
          <span style={{ color: 'var(--text-muted)' }}>Source</span>
          <select
            value={sourceFilter}
            onChange={(e) => {
              const v = e.target.value as 'all' | 'ipscape' | 'wildix';
              setSourceFilter(v);
              setRefreshing(true);
              void loadLogs(v)
                .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'))
                .finally(() => setRefreshing(false));
            }}
            style={{
              padding: '0.35rem 0.5rem',
              borderRadius: 6,
              border: '1px solid rgba(255,255,255,0.15)',
              background: 'var(--widget-bg)',
              color: 'var(--text)',
            }}
          >
            <option value="all">All</option>
            <option value="ipscape">ipSCAPE</option>
            <option value="wildix">Wildix</option>
          </select>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
          <span style={{ color: 'var(--text-muted)' }}>Retention (days)</span>
          <input
            type="number"
            min={1}
            max={30}
            value={retentionDays}
            onChange={(e) => setRetentionDays(parseInt(e.target.value, 10) || 7)}
            style={{
              width: 64,
              padding: '0.35rem 0.5rem',
              borderRadius: 6,
              border: '1px solid rgba(255,255,255,0.15)',
              background: 'var(--widget-bg)',
              color: 'var(--text)',
            }}
          />
        </label>
        <button
          type="button"
          onClick={() => void saveRetention()}
          disabled={savingRetention}
          style={{
            padding: '0.35rem 0.75rem',
            background: 'var(--accent)',
            border: 'none',
            borderRadius: 6,
            color: '#fff',
            cursor: savingRetention ? 'not-allowed' : 'pointer',
            fontSize: '0.875rem',
          }}
        >
          {savingRetention ? 'Saving…' : 'Save retention'}
        </button>
        {retentionMsg && <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>{retentionMsg}</span>}
        <button
          type="button"
          onClick={() => {
            setRefreshing(true);
            setError(null);
            void loadLogs()
              .catch((e) => setError(e instanceof Error ? e.message : 'Refresh failed'))
              .finally(() => setRefreshing(false));
          }}
          disabled={refreshing}
          style={{
            padding: '0.35rem 0.75rem',
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 6,
            color: 'var(--text)',
            cursor: refreshing ? 'not-allowed' : 'pointer',
            fontSize: '0.875rem',
          }}
        >
          {refreshing ? 'Refreshing…' : 'Refresh table'}
        </button>
      </div>

      {error && <p style={{ color: '#ef4444', fontSize: '0.875rem', marginBottom: '1rem' }}>{error}</p>}
      {loading ? (
        <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
      ) : rows.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
          No webhook events logged yet. After deploy, wrap a call in ipSCAPE or trigger Wildix presence — then refresh.
        </p>
      ) : (
        <>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', marginBottom: '0.5rem' }}>
            Showing {rows.length} of {total} rows (latest first).
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Time</th>
                  <th style={thStyle}>Source</th>
                  <th style={thStyle}>Event</th>
                  <th style={thStyle}>OK</th>
                  <th style={thStyle}>Reason</th>
                  <th style={thStyle}>Activity</th>
                  <th style={thStyle}>Agent</th>
                  <th style={thStyle}>Campaign</th>
                  <th style={thStyle}>HTTP</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td style={tdStyle}>{formatLocal(r.created_at)}</td>
                    <td style={tdStyle}>{r.source}</td>
                    <td style={tdStyle}>{r.event_type ?? '—'}</td>
                    <td style={{ ...tdStyle, color: r.ok ? '#86efac' : '#fca5a5' }}>{r.ok ? 'Yes' : 'No'}</td>
                    <td style={tdStyle}>{r.reason ?? '—'}</td>
                    <td style={tdStyle}>{r.activity_id ?? '—'}</td>
                    <td style={tdStyle}>{r.agent_id ?? '—'}</td>
                    <td style={tdStyle}>{r.campaign_id ?? '—'}</td>
                    <td style={tdStyle}>{r.http_status ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div
      style={{
        padding: '0.65rem 0.75rem',
        background: highlight ? 'rgba(239, 68, 68, 0.1)' : 'var(--widget-bg)',
        borderRadius: 8,
        border: `1px solid ${highlight ? 'rgba(239, 68, 68, 0.35)' : 'rgba(255,255,255,0.08)'}`,
      }}
    >
      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.2rem' }}>{label}</div>
      <div style={{ fontSize: '1.125rem', fontWeight: 700 }}>{value}</div>
    </div>
  );
}
